import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, Loader2, Send } from 'lucide-react';
import { Button } from 'components/ui/button';
import { getPortalPaymentDetails } from 'modules/portal-plans/api';
import { PAYMENT_SUPPORT_TELEGRAM } from 'modules/payments/methods';
import { formatSom } from './billingHelpers';

interface CardTransferPanelProps {
  /** What the transfer pays for, quoted in the receipt message. */
  purpose: string;
  amountSom: number;
}

/**
 * The P2P card transfer: the card set by platform admins, the amount, and a Telegram link for the
 * receipt. Nothing is charged here; an admin activates the plan once the receipt is checked.
 */
export function CardTransferPanel({ purpose, amountSom }: CardTransferPanelProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const details = useQuery({
    queryKey: ['portal-payment-details'],
    queryFn: ({ signal }) => getPortalPaymentDetails(signal),
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  if (details.isLoading) return <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden />;
  if (details.isError || !details.data) {
    return (
      <p role="alert" className="text-sm text-muted-foreground">
        {t('card_transfer_unavailable', 'Card transfer is not available right now. Please choose another method.')}
      </p>
    );
  }

  const { cardNumber, cardHolderName } = details.data;
  const telegramUrl = `${PAYMENT_SUPPORT_TELEGRAM}?text=${encodeURIComponent(
    `Assalomu alaykum! Examing: ${purpose} uchun ${formatSom(amountSom)} to'lov qildim. Chekni ilova qilaman.`,
  )}`;

  return (
    <div className="space-y-3">
      <div className="rounded-lg bg-neutral-900 p-4 text-white">
        <span className="block text-[11px] font-semibold uppercase tracking-widest text-neutral-400">
          {t('card_transfer_card', 'Card number')}
        </span>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="font-mono text-lg font-bold tracking-widest">{cardNumber.replace(/(\d{4})(?=\d)/g, '$1 ')}</span>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(cardNumber).then(() => setCopied(true), () => undefined);
            }}
            aria-label={t('card_transfer_copy', 'Copy card number')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white/15 transition-colors hover:bg-white/25"
          >
            {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
        <span className="mt-1 block truncate text-xs font-semibold uppercase tracking-wide text-neutral-300">
          {cardHolderName}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">
        {t('card_transfer_steps', {
          defaultValue:
            'Transfer {{amount}} to this card, then send the receipt on Telegram. We activate it after checking the payment, usually within a few minutes.',
          amount: formatSom(amountSom),
        })}
      </p>
      <Button asChild className="w-full gap-1.5">
        <a href={telegramUrl} target="_blank" rel="noopener noreferrer">
          <Send className="h-4 w-4" aria-hidden />
          {t('card_transfer_send_receipt', 'Send the receipt on Telegram')}
        </a>
      </Button>
    </div>
  );
}
